import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { FiArrowLeft, FiHome, FiSave, FiAlertCircle } from 'react-icons/fi';
import { apiClient } from '../../services/api';
import { useServiceTypes } from '../../hooks/useServiceTypes';
import { useDialog } from '../../hooks/useDialog';
import toast from 'react-hot-toast';
import MapPicker from '../../components/maps/MapPicker';

// Thai provinces list
const THAI_PROVINCES = [
  'กรุงเทพมหานคร', 'กระบี่', 'กาญจนบุรี', 'กาฬสินธุ์', 'กำแพงเพชร', 'ขอนแก่น', 'จันทบุรี', 'ฉะเชิงเทรา',
  'ชลบุรี', 'ชัยนาท', 'ชัยภูมิ', 'ชุมพร', 'เชียงราย', 'เชียงใหม่', 'ตรัง', 'ตราด', 'ตาก', 'นครนายก',
  'นครปฐม', 'นครพนม', 'นครราชสีมา', 'นครศรีธรรมราช', 'นครสวรรค์', 'นนทบุรี', 'นราธิวาส', 'น่าน',
  'บึงกาฬ', 'บุรีรัมย์', 'ปทุมธานี', 'ประจวบคีรีขันธ์', 'ปราจีนบุรี', 'ปัตตานี', 'พระนครศรีอยุธยา',
  'พังงา', 'พัทลุง', 'พิจิตร', 'พิษณุโลก', 'เพชรบุรี', 'เพชรบูรณ์', 'แพร่', 'พะเยา', 'ภูเก็ต',
  'มหาสารคาม', 'มุกดาหาร', 'แม่ฮ่องสอน', 'ยโสธร', 'ยะลา', 'ร้อยเอ็ด', 'ระนอง', 'ระยอง', 'ราชบุรี',
  'ลพบุรี', 'ลำปาง', 'ลำพูน', 'เลย', 'ศรีสะเกษ', 'สกลนคร', 'สงขลา', 'สตูล', 'สมุทรปราการ',
  'สมุทรสงคราม', 'สมุทรสาคร', 'สระแก้ว', 'สระบุรี', 'สิงห์บุรี', 'สุโขทัย', 'สุพรรณบุรี', 'สุราษฎร์ธานี',
  'สุรินทร์', 'หนองคาย', 'หนองบัวลำภู', 'อ่างทอง', 'อำนาจเจริญ', 'อุดรธานี', 'อุตรดิตถ์', 'อุทัยธานี', 'อุบลราชธานี'
];

// Company sizes
const COMPANY_SIZES = [
  { value: 'SMALL', label: 'เล็ก (1-50 คน)' },
  { value: 'MEDIUM', label: 'กลาง (51-200 คน)' },
  { value: 'LARGE', label: 'ใหญ่ (201+ คน)' }
];

interface FormData {
  nameTh: string;
  nameEn: string;
  description: string;
  phone1: string;
  phone2: string;
  phone3: string;
  email: string;
  website: string;
  lineId: string;
  facebookUrl: string;
  address: string;
  province: string;
  district: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  serviceTypes: string[];
  serviceAreas: string[];
  companySize: string;
  establishedYear: string;
  licenseNumber: string;
  registrationId: string;
  taxId: string;
  logoUrl: string;
  isActive: boolean;
  isFeatured: boolean;
  isVerified: boolean;
  version: number; // BUG-021 FIX: Add version for optimistic locking
}

const CompanyEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');

  // Fetch service types from API
  const { serviceTypes, loading: serviceTypesLoading, error: serviceTypesError } = useServiceTypes(true);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    nameTh: '',
    nameEn: '',
    description: '',
    phone1: '',
    phone2: '',
    phone3: '',
    email: '',
    website: '',
    lineId: '',
    facebookUrl: '',
    address: '',
    province: '',
    district: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    serviceTypes: [],
    serviceAreas: [],
    companySize: '',
    establishedYear: '',
    licenseNumber: '',
    registrationId: '',
    taxId: '',
    logoUrl: '',
    isActive: true,
    isFeatured: false,
    isVerified: false,
    version: 1 // BUG-021 FIX: Initialize version
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Gallery images state
  interface GalleryImage {
    id: string;
    imageUrl: string;
    displayOrder: number;
  }
  const [existingGalleryImages, setExistingGalleryImages] = useState<GalleryImage[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState<File[]>([]);
  const [newGalleryPreviews, setNewGalleryPreviews] = useState<string[]>([]);

  const breadcrumbs = [
    { label: 'จัดการบริษัท', icon: FiHome, path: '/admin/companies' },
    { label: 'แก้ไขข้อมูลบริษัท', icon: FiSave }
  ];

  // Fetch company data on mount
  useEffect(() => {
    const fetchCompany = async () => {
      if (!id) {
        setError('ไม่พบ ID บริษัท');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await apiClient.get(`/api/admin/companies/${id}`);
        const company = response.data;

        // BUG FIX: Convert service type names back to IDs
        // Backend returns names, but checkboxes need IDs
        let serviceTypeIds: string[] = [];
        if (company.serviceTypes && Array.isArray(company.serviceTypes)) {
          // Wait for serviceTypes to load first
          if (!serviceTypesLoading && serviceTypes.length > 0) {
            serviceTypeIds = company.serviceTypes
              .map((name: string) => {
                const found = serviceTypes.find(st => st.nameTh === name);
                return found ? found.id : null;
              })
              .filter((id: string | null): id is string => id !== null);
          } else {
            // Store names temporarily, will convert when serviceTypes load
            serviceTypeIds = company.serviceTypes;
          }
        }

        // Populate form with existing data
        setFormData({
          nameTh: company.nameTh || '',
          nameEn: company.nameEn || '',
          description: company.description || '',
          phone1: company.phone1 || '',
          phone2: company.phone2 || '',
          phone3: company.phone3 || '',
          email: company.email || '',
          website: company.website || '',
          lineId: company.lineId || '',
          facebookUrl: company.facebookUrl || '',
          address: company.address || '',
          province: company.province || '',
          district: company.district || '',
          postalCode: company.postalCode || '',
          latitude: company.latitude ? String(company.latitude) : '',
          longitude: company.longitude ? String(company.longitude) : '',
          serviceTypes: serviceTypeIds,  // Use converted IDs
          serviceAreas: company.serviceAreas || [],  // serviceAreas already as province names
          companySize: company.companySize || '',
          establishedYear: company.establishedYear ? String(company.establishedYear) : '',
          licenseNumber: company.licenseNumber || '',
          registrationId: company.registrationId || '',
          taxId: company.taxId || '',
          logoUrl: company.logoUrl || '',
          isActive: company.isActive ?? true,
          isFeatured: company.isFeatured ?? false,
          isVerified: company.isVerified ?? false,
          version: company.version || 1 // BUG-021 FIX: Load version from API
        });

        setCompanyName(company.nameTh || 'บริษัท');

        // Load existing gallery images
        if (company.galleryImages && Array.isArray(company.galleryImages)) {
          setExistingGalleryImages(company.galleryImages);
        }

        setError(null);
      } catch (err: any) {
        console.error('Error fetching company:', err);
        if (err.response?.status === 404) {
          setError('ไม่พบบริษัทที่ต้องการแก้ไข');
        } else if (err.response?.status === 401) {
          setError('กรุณาเข้าสู่ระบบใหม่');
        } else if (err.response?.status === 403) {
          setError('คุณไม่มีสิทธิ์ในการแก้ไขบริษัท');
        } else {
          setError('ไม่สามารถโหลดข้อมูลบริษัทได้');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleServiceTypeToggle = (serviceTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceTypeId)
        ? prev.serviceTypes.filter(t => t !== serviceTypeId)
        : [...prev.serviceTypes, serviceTypeId]
    }));
  };

  const handleServiceAreaToggle = (area: string) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(area)
        ? prev.serviceAreas.filter(a => a !== area)
        : [...prev.serviceAreas, area]
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.nameTh.trim()) {
      errors.nameTh = 'กรุณากรอกชื่อบริษัท (ภาษาไทย)';
    }
    if (!formData.phone1.trim()) {
      errors.phone1 = 'กรุณากรอกเบอร์โทรศัพท์หลัก';
    }
    if (!formData.email.trim()) {
      errors.email = 'กรุณากรอกอีเมล';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }
    if (!formData.address.trim()) {
      errors.address = 'กรุณากรอกที่อยู่';
    }
    if (!formData.province) {
      errors.province = 'กรุณาเลือกจังหวัด';
    }
    if (!formData.district.trim()) {
      errors.district = 'กรุณากรอกเขต/อำเภอ';
    }

    // Validate year if provided
    if (formData.establishedYear) {
      const year = parseInt(formData.establishedYear);
      if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
        errors.establishedYear = 'ปีที่ก่อตั้งไม่ถูกต้อง';
      }
    }

    // Validate coordinates if provided
    if (formData.latitude && (isNaN(parseFloat(formData.latitude)) || parseFloat(formData.latitude) < -90 || parseFloat(formData.latitude) > 90)) {
      errors.latitude = 'ละติจูดต้องอยู่ระหว่าง -90 ถึง 90';
    }
    if (formData.longitude && (isNaN(parseFloat(formData.longitude)) || parseFloat(formData.longitude) < -180 || parseFloat(formData.longitude) > 180)) {
      errors.longitude = 'ลองจิจูดต้องอยู่ระหว่าง -180 ถึง 180';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) {
      setError('กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use FormData for file upload support
      const formDataToSend = new FormData();

      // Add all text fields
      formDataToSend.append('nameTh', formData.nameTh.trim());
      if (formData.nameEn.trim()) formDataToSend.append('nameEn', formData.nameEn.trim());
      if (formData.description.trim()) formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('phone1', formData.phone1.trim());
      if (formData.phone2.trim()) formDataToSend.append('phone2', formData.phone2.trim());
      if (formData.phone3.trim()) formDataToSend.append('phone3', formData.phone3.trim());
      formDataToSend.append('email', formData.email.trim());
      if (formData.website.trim()) formDataToSend.append('website', formData.website.trim());
      if (formData.lineId.trim()) formDataToSend.append('lineId', formData.lineId.trim());
      if (formData.facebookUrl.trim()) formDataToSend.append('facebookUrl', formData.facebookUrl.trim());
      formDataToSend.append('address', formData.address.trim());
      formDataToSend.append('province', formData.province);
      formDataToSend.append('district', formData.district.trim());
      if (formData.postalCode.trim()) formDataToSend.append('postalCode', formData.postalCode.trim());
      if (formData.companySize) formDataToSend.append('companySize', formData.companySize);
      if (formData.licenseNumber.trim()) formDataToSend.append('licenseNumber', formData.licenseNumber.trim());
      if (formData.registrationId.trim()) formDataToSend.append('registrationId', formData.registrationId.trim());
      if (formData.taxId.trim()) formDataToSend.append('taxId', formData.taxId.trim());
      if (formData.logoUrl.trim()) formDataToSend.append('logoUrl', formData.logoUrl.trim());

      // Add boolean fields
      formDataToSend.append('isActive', String(formData.isActive));
      formDataToSend.append('isFeatured', String(formData.isFeatured));
      formDataToSend.append('isVerified', String(formData.isVerified));

      // Add version for optimistic locking
      formDataToSend.append('version', String(formData.version));

      // Add numeric fields
      if (formData.establishedYear) formDataToSend.append('establishedYear', formData.establishedYear);
      if (formData.latitude) formDataToSend.append('latitude', formData.latitude);
      if (formData.longitude) formDataToSend.append('longitude', formData.longitude);

      // Add arrays as JSON strings
      if (formData.serviceTypes.length > 0) {
        formDataToSend.append('serviceTypes', JSON.stringify(formData.serviceTypes));
      }
      if (formData.serviceAreas.length > 0) {
        formDataToSend.append('serviceAreas', JSON.stringify(formData.serviceAreas));
      }

      // Add deleted image IDs if any
      if (deletedImageIds.length > 0) {
        formDataToSend.append('deletedImageIds', JSON.stringify(deletedImageIds));
      }

      // Add new gallery images if any (MERGE - backend will add these to existing)
      if (newGalleryFiles.length > 0) {
        newGalleryFiles.forEach(file => {
          formDataToSend.append('gallery_images', file);
        });
      }

      const response = await apiClient.put(`/api/admin/companies/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200) {
        setSuccess(true);
        // Redirect to company detail page after short delay
        setTimeout(() => {
          navigate(`/admin/companies/view/${id}`);
        }, 1500);
      } else {
        setError('เกิดข้อผิดพลาดในการแก้ไขบริษัท');
      }
    } catch (err: any) {
      console.error('Error updating company:', err);

      // BUG-021 FIX: Handle 409 Conflict (concurrent update)
      if (err.response?.status === 409) {
        const conflictDetail = err.response?.data?.detail;
        let conflictMessage = 'บริษัทนี้ถูกแก้ไขโดยผู้อื่นแล้ว กรุณาโหลดข้อมูลใหม่';

        if (typeof conflictDetail === 'object' && conflictDetail.message) {
          conflictMessage = conflictDetail.message;
        } else if (typeof conflictDetail === 'string') {
          conflictMessage = conflictDetail;
        }

        await showConfirm(
          {
            title: 'ข้อมูลถูกแก้ไขโดยผู้อื่น',
            message: conflictMessage,
            confirmText: 'โหลดข้อมูลใหม่',
            cancelText: 'ยกเลิก',
            variant: 'warning'
          },
          () => {
            // Reload page to get latest data
            window.location.reload();
          }
        );
      } else if (err.response?.data?.detail) {
        setError(`เกิดข้อผิดพลาด: ${err.response.data.detail}`);
      } else if (err.response?.status === 401) {
        setError('กรุณาเข้าสู่ระบบใหม่');
      } else if (err.response?.status === 403) {
        setError('คุณไม่มีสิทธิ์ในการแก้ไขบริษัท');
      } else if (err.response?.status === 404) {
        setError('ไม่พบบริษัทที่ต้องการแก้ไข');
      } else {
        setError('ไม่สามารถแก้ไขบริษัทได้ กรุณาลองใหม่อีกครั้ง');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('เบราว์เซอร์ของคุณไม่รองรับ GPS');
      return;
    }

    toast.loading('กำลังดึงตำแหน่ง GPS...', { id: 'gps-loading' });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = parseFloat(position.coords.latitude.toFixed(8));
        const lng = parseFloat(position.coords.longitude.toFixed(8));

        setFormData(prev => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lng.toString()
        }));

        // Validate Thailand bounds
        const isInThailand = lat >= 5.61 && lat <= 20.46 && lng >= 97.34 && lng <= 105.64;
        if (!isInThailand) {
          toast.error('ตำแหน่งที่ได้อยู่นอกประเทศไทย กรุณาตรวจสอบอีกครั้ง', { id: 'gps-loading' });
        } else {
          toast.success(`ตำแหน่ง GPS ถูกบันทึกแล้ว: ${lat.toFixed(6)}, ${lng.toFixed(6)}`, { id: 'gps-loading' });
        }
      },
      (error) => {
        console.error('GPS Error:', error);
        toast.error('ไม่สามารถเข้าถึงตำแหน่ง GPS ได้ กรุณาอนุญาตการเข้าถึงตำแหน่ง', { id: 'gps-loading' });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(8),
      longitude: lng.toFixed(8)
    }));
  };

  const handleDeleteExistingImage = (imageId: string) => {
    // Add to deleted list
    setDeletedImageIds(prev => [...prev, imageId]);
    // Remove from existing list (visual feedback)
    setExistingGalleryImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleNewGalleryFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    // Calculate total images (existing - deleted + new + newly selected)
    const remainingExisting = existingGalleryImages.length;
    const totalImages = remainingExisting + newGalleryFiles.length + files.length;

    if (totalImages > 6) {
      setError(`สามารถมีได้สูงสุด 6 รูป (มีอยู่แล้ว: ${remainingExisting} รูป, เลือกใหม่: ${newGalleryFiles.length} รูป)`);
      return;
    }

    // Validate file types and sizes
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError(`กรุณาเลือกไฟล์รูปภาพเท่านั้น (${file.name})`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`ขนาดไฟล์ต้องไม่เกิน 5MB (${file.name})`);
        return;
      }
    }

    setError(null);

    // Add files to state
    setNewGalleryFiles(prev => [...prev, ...files]);

    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewGalleryPreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveNewGalleryImage = (index: number) => {
    setNewGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setNewGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleBack = async () => {
    await showConfirm(
      {
        title: 'ยืนยันการยกเลิก',
        message: 'คุณต้องการยกเลิกการแก้ไขใช่หรือไม่? การเปลี่ยนแปลงจะไม่ถูกบันทึก',
        confirmText: 'ยกเลิก',
        cancelText: 'กลับไปแก้ไข',
        variant: 'warning'
      },
      () => {
        navigate('/admin/companies');
      }
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout title="แก้ไขข้อมูลบริษัท" breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">กำลังโหลดข้อมูลบริษัท...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state (if can't load company)
  if (error && !formData.nameTh) {
    return (
      <AdminLayout title="แก้ไขข้อมูลบริษัท" breadcrumbs={breadcrumbs}>
        <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
          <div className="flex items-start">
            <FiAlertCircle className="h-6 w-6 text-red-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-900 mb-2">
                {error}
              </h3>
              <button
                onClick={() => navigate('/admin/companies')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiArrowLeft className="mr-2 h-4 w-4" />
                กลับไปหน้ารายการ
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`แก้ไขข้อมูล: ${companyName}`} breadcrumbs={breadcrumbs}>
      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                แก้ไขข้อมูลบริษัทสำเร็จ! กำลังนำคุณไปยังหน้ารายละเอียด...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && formData.nameTh && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex items-center">
            <FiAlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">1</span>
            ข้อมูลพื้นฐาน
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ชื่อบริษัท (ภาษาไทย) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nameTh"
                value={formData.nameTh}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.nameTh ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="บริษัท ไทยซีเคียวริตี้ จำกัด"
              />
              {validationErrors.nameTh && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.nameTh}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ชื่อบริษัท (ภาษาอังกฤษ)
              </label>
              <input
                type="text"
                name="nameEn"
                value={formData.nameEn}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Thai Security Co., Ltd."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                คำอธิบายบริษัท
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="ให้บริการรักษาความปลอดภัยครบวงจร..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ปีที่ก่อตั้ง
              </label>
              <input
                type="number"
                name="establishedYear"
                value={formData.establishedYear}
                onChange={handleInputChange}
                min="1900"
                max={new Date().getFullYear()}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.establishedYear ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="2020"
              />
              {validationErrors.establishedYear && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.establishedYear}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ขนาดบริษัท
              </label>
              <select
                name="companySize"
                value={formData.companySize}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">เลือกขนาดบริษัท</option>
                {COMPANY_SIZES.map(size => (
                  <option key={size.value} value={size.value}>{size.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL โลโก้
              </label>
              <input
                type="text"
                name="logoUrl"
                value={formData.logoUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com/logo.png หรือ /uploads/logos/file.jpg"
              />
              <p className="mt-1 text-xs text-gray-500">
                รองรับ Full URL (https://...) หรือ Relative Path (/uploads/...)
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">2</span>
            ข้อมูลติดต่อ
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เบอร์โทรศัพท์หลัก <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone1"
                value={formData.phone1}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.phone1 ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="02-123-4567"
              />
              {validationErrors.phone1 && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.phone1}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เบอร์โทรศัพท์สำรอง 1
              </label>
              <input
                type="tel"
                name="phone2"
                value={formData.phone2}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="089-123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เบอร์โทรศัพท์สำรอง 2
              </label>
              <input
                type="tel"
                name="phone3"
                value={formData.phone3}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="088-123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                อีเมล <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="info@company.com"
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เว็บไซต์
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://www.company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LINE ID
              </label>
              <input
                type="text"
                name="lineId"
                value={formData.lineId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="@companyline"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facebook URL
              </label>
              <input
                type="url"
                name="facebookUrl"
                value={formData.facebookUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://facebook.com/company"
              />
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">3</span>
            ที่อยู่
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ที่อยู่ <span className="text-red-500">*</span>
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.address ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="123 ถนนสุขุมวิท แขวงคลองเตย"
              />
              {validationErrors.address && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                จังหวัด <span className="text-red-500">*</span>
              </label>
              <select
                name="province"
                value={formData.province}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.province ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">เลือกจังหวัด</option>
                {THAI_PROVINCES.map(province => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
              {validationErrors.province && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.province}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เขต/อำเภอ <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.district ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="คลองเตย"
              />
              {validationErrors.district && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.district}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                รหัสไปรษณีย์
              </label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="10110"
                maxLength={5}
              />
            </div>

            {/* GPS Coordinates Section */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  พิกัด GPS (ตำแหน่งบนแผนที่)
                </label>
                <button
                  type="button"
                  onClick={handleGetCurrentLocation}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  ใช้ตำแหน่งปัจจุบันของฉัน
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    ละติจูด (Latitude) <span className="text-gray-400">-90 ถึง 90</span>
                  </label>
                  <input
                    type="number"
                    step="0.00000001"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                      validationErrors.latitude ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="13.7563000"
                  />
                  {validationErrors.latitude && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.latitude}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    ลองจิจูด (Longitude) <span className="text-gray-400">-180 ถึง 180</span>
                  </label>
                  <input
                    type="number"
                    step="0.00000001"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                      validationErrors.longitude ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="100.5018000"
                  />
                  {validationErrors.longitude && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.longitude}</p>
                  )}
                </div>
              </div>

              {/* Thailand GPS Validation Warning */}
              {formData.latitude && formData.longitude && (
                (() => {
                  const lat = parseFloat(formData.latitude);
                  const lng = parseFloat(formData.longitude);
                  const isInThailand = lat >= 5.61 && lat <= 20.46 && lng >= 97.34 && lng <= 105.64;

                  if (!isNaN(lat) && !isNaN(lng) && !isInThailand) {
                    return (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2">
                        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">ตำแหน่ง GPS อยู่นอกประเทศไทย</p>
                          <p className="text-xs text-amber-700 mt-1">
                            พิกัดที่กรอกอยู่นอกขอบเขตประเทศไทย (Lat: 5.61-20.46, Lng: 97.34-105.64) กรุณาตรวจสอบอีกครั้ง
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()
              )}

              {/* Map Picker */}
              {formData.latitude && formData.longitude && (
                <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    คลิกบนแผนที่เพื่อเลือกตำแหน่งที่แม่นยำ:
                  </p>
                  <MapPicker
                    latitude={parseFloat(formData.latitude) || 13.7563}
                    longitude={parseFloat(formData.longitude) || 100.5018}
                    onLocationSelect={handleMapLocationSelect}
                    height="400px"
                  />
                </div>
              )}

              {!formData.latitude && !formData.longitude && (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="font-medium mb-1">วิธีการกรอกพิกัด GPS:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>คลิกปุ่ม "ใช้ตำแหน่งปัจจุบันของฉัน" เพื่อใช้ GPS อุปกรณ์</li>
                    <li>หรือกรอกพิกัดด้วยตนเอง แล้วคลิกบนแผนที่เพื่อปรับตำแหน่ง</li>
                    <li>พิกัด GPS จะช่วยให้ผู้ใช้ค้นหาบริษัทตามระยะทางได้</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Service Types */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">4</span>
            ประเภทบริการ
          </h3>

          {serviceTypesError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-red-800">
                <p className="font-semibold">ไม่สามารถโหลดประเภทบริการได้</p>
                <p className="text-xs mt-1">{serviceTypesError}</p>
              </div>
            </div>
          )}

          {serviceTypesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-gray-600">กำลังโหลดประเภทบริการ...</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {serviceTypes.map(serviceType => (
                <label key={serviceType.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={formData.serviceTypes.includes(serviceType.id)}
                    onChange={() => handleServiceTypeToggle(serviceType.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{serviceType.nameTh}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Service Areas */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">5</span>
            พื้นที่ให้บริการ
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {THAI_PROVINCES.map(province => (
              <label key={province} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                <input
                  type="checkbox"
                  checked={formData.serviceAreas.includes(province)}
                  onChange={() => handleServiceAreaToggle(province)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{province}</span>
              </label>
            ))}
          </div>
        </div>

        {/* License Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">6</span>
            ข้อมูลใบอนุญาตและทะเบียน
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เลขที่ใบอนุญาต
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="SEC-001-2023"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เลขทะเบียนนิติบุคคล
              </label>
              <input
                type="text"
                name="registrationId"
                value={formData.registrationId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="0105123456789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                เลขประจำตัวผู้เสียภาษี
              </label>
              <input
                type="text"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="0105123456789"
              />
            </div>
          </div>
        </div>

        {/* Company Status (Admin Only) */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">7</span>
            สถานะบริษัท (Admin)
          </h3>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-3 block text-sm font-medium text-gray-700">
                เปิดใช้งาน (Active)
                <span className="ml-2 text-gray-500 text-xs">- บริษัทจะแสดงในระบบค้นหา</span>
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-3 block text-sm font-medium text-gray-700">
                แนะนำ (Featured)
                <span className="ml-2 text-gray-500 text-xs">- บริษัทจะแสดงในหน้าแรก</span>
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isVerified"
                checked={formData.isVerified}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-3 block text-sm font-medium text-gray-700">
                ยืนยันแล้ว (Verified)
                <span className="ml-2 text-gray-500 text-xs">- แสดง badge ยืนยันบนหน้าบริษัท</span>
              </label>
            </div>
          </div>
        </div>

        {/* Gallery Images Management */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">8</span>
            รูปภาพบริษัท (Gallery)
          </h3>

          {/* Existing Images */}
          {existingGalleryImages.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">รูปภาพที่มีอยู่แล้ว ({existingGalleryImages.length} รูป)</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {existingGalleryImages.map((image, index) => (
                  <div key={image.id} className="relative group">
                    <img
                      src={image.imageUrl}
                      alt={`Gallery ${index + 1}`}
                      className="h-32 w-full object-cover border-2 border-gray-300 rounded-lg"
                    />
                    <div className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingImage(image.id)}
                        className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {image.displayOrder + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Images Preview */}
          {newGalleryPreviews.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">รูปภาพใหม่ที่เลือก ({newGalleryPreviews.length} รูป)</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {newGalleryPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`New ${index + 1}`}
                      className="h-32 w-full object-cover border-2 border-green-400 rounded-lg"
                    />
                    <div className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveNewGalleryImage(index)}
                        className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="absolute top-2 left-2 bg-green-600 text-white text-xs font-bold rounded-full px-2 py-0.5">
                      ใหม่
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Upload Button */}
          <div className="space-y-3">
            <label className="block">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleNewGalleryFilesChange}
                className="hidden"
                id="gallery-upload-edit"
                disabled={existingGalleryImages.length + newGalleryFiles.length >= 6}
              />
              <div
                className={`cursor-pointer inline-flex items-center px-6 py-3 border-2 border-dashed rounded-lg shadow-sm text-sm font-medium transition-colors ${
                  existingGalleryImages.length + newGalleryFiles.length >= 6
                    ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-400'
                }`}
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {existingGalleryImages.length + newGalleryFiles.length >= 6 ? 'มีรูปภาพครบ 6 รูปแล้ว' : 'เลือกรูปภาพเพิ่มเติม (หลายไฟล์)'}
              </div>
            </label>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1 text-xs text-blue-800 space-y-1">
                  <p className="font-medium">คำแนะนำการจัดการรูปภาพ:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li>คลิกปุ่ม X บนรูปเก่าเพื่อลบ (รูปจะถูกลบเมื่อกด บันทึกการเปลี่ยนแปลง)</li>
                    <li>สามารถเพิ่มรูปใหม่ได้ (สูงสุด 6 รูปรวมกัน)</li>
                    <li>รูปใหม่จะถูกเพิ่มต่อจากรูปเก่าที่เหลืออยู่</li>
                    <li>รองรับไฟล์: JPG, PNG, GIF (ขนาดไม่เกิน 5MB ต่อรูป)</li>
                  </ul>
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="font-semibold">รูปทั้งหมด:</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      existingGalleryImages.length + newGalleryFiles.length === 0 ? 'bg-gray-200 text-gray-600' :
                      existingGalleryImages.length + newGalleryFiles.length >= 6 ? 'bg-green-200 text-green-800' :
                      'bg-blue-200 text-blue-800'
                    }`}>
                      {existingGalleryImages.length + newGalleryFiles.length} / 6 รูป
                    </span>
                    {deletedImageIds.length > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-200 text-red-800">
                        รอลบ: {deletedImageIds.length} รูป
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between bg-gray-50 px-6 py-4 rounded-lg">
          <button
            type="button"
            onClick={handleBack}
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiArrowLeft className="mr-2 h-4 w-4" />
            ยกเลิก
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                กำลังบันทึก...
              </>
            ) : (
              <>
                <FiSave className="mr-2 h-4 w-4" />
                บันทึกการเปลี่ยนแปลง
              </>
            )}
          </button>
        </div>
      </form>

      {ConfirmDialog}
      {AlertDialog}
    </AdminLayout>
  );
};

export default CompanyEditPage;
